import { useMemo, useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { Box, Button, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { navigate, useParams } from '@redwoodjs/router';
import { useMutation, useQuery } from '@redwoodjs/web';

import { CustomRadioGroup } from 'src/components/FormikComponents/CustomRadioGroup';
import Loader from 'src/components/Loader/Loader';
import { ModalBox } from 'src/components/ModalContent/Modal';
import NPISingleDatePicker from 'src/components/NPISingleDatePicker/NPISingleDatePicker';
import { TextAreaInput } from 'src/components/TextAreaInput';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import ViewPDFModal from 'src/components/ViewPDFModal';
import { GET_ADDRESS_COORDINATES } from 'src/graphql/queries/addressCoordinates';
import {
    DELETE_CHECK_IN_CALL_SUCCESS_BY_ID,
    GET_CHECK_IN_CALL_SUCCESS,
    UPDATE_CHECK_IN_CALL_SUCCESS_CALL_DATE,
} from 'src/graphql/queries/getCheckInCalls.query';
import useCreateNote from 'src/hooks/useCreateNote';
import useMerchantApplication from 'src/hooks/useMerchantApplication';
import { onError } from 'src/utils/utils';

import OutletSection from '../ViewServicePage/components/OutletSection';

import MerchantInfoColumn from './components/MerchantInfoColumn';
import NotesSection from './components/NotesSection';
import ServiceDetailsSection from './components/ServiceDetailsSection';
import TitleSection from './components/TitleSection';

const FOLLOW_UP_OPTIONS = [
    { label: 'No', value: 'NO' },
    { label: 'Yes', value: 'YES' },
];

const OPTION_TYPES = ['NO', 'YES'];

const CheckInCallDashboardPage = () => {
    const { serviceId, checkInCallId } = useParams();
    const [isOpen, setIsOpen] = useState(false);
    const { data, loading } = useMerchantApplication(Number(serviceId));
    const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);
    const noteData = useMemo(() => {
        if (data?.merchant?.id) {
            return {
                merchantId: data?.merchant.id,
                checkInCallServiceId: Number.parseInt(serviceId, 10),
            };
        }
        return {
            partnerId: data?.partnerId,
            checkInCallServiceId: Number.parseInt(serviceId, 10),
        };
    }, [data?.merchant?.id, data?.partnerId, serviceId]);

    const { onCreateNote } = useCreateNote(noteData);
    const today = dayjs();

    const { data: checkInCallResponse } = useQuery(GET_CHECK_IN_CALL_SUCCESS, {
        skip: checkInCallId === 'checkInId',
        fetchPolicy: 'network-only',
        variables: {
            id: Number.parseInt(checkInCallId, 10),
        },
    });
    const client = useApolloClient();
    const [deleteSuccessCheckInCall, { loading: isDeleting }] = useMutation(
        DELETE_CHECK_IN_CALL_SUCCESS_BY_ID,
        {
            refetchQueries: [GET_CHECK_IN_CALL_SUCCESS],
            onCompleted: (data) => {
                if (data?.deleteSuccessCheckInCall?.success) {
                    client.refetchQueries({ include: [GET_CHECK_IN_CALL_SUCCESS] });
                    showSuccessToast(data?.deleteSuccessCheckInCall?.message);
                    navigate('/customer-success/check-in-calls');
                }
            },
            onError,
        }
    );

    const [updateSuccessCheckInCall, { loading: isEditing }] = useMutation(
        UPDATE_CHECK_IN_CALL_SUCCESS_CALL_DATE,
        {
            refetchQueries: [GET_CHECK_IN_CALL_SUCCESS],
            onCompleted: (data) => {
                if (data?.updateSuccessScheduledCallDate?.success) {
                    client.refetchQueries({ include: [GET_CHECK_IN_CALL_SUCCESS] });
                    showSuccessToast(data?.updateSuccessScheduledCallDate?.message);
                    navigate('/customer-success/check-in-calls');
                }
            },
            onError,
        }
    );

    const handleCloseCallSubmit = async (values) => {
        await onCreateNote({
            description: values?.cicNotes,
            subject: checkInCallResponse?.getSuccessCheckInCall?.callName,
        });

        if (values?.cicFollowUp === 'YES') {
            await updateSuccessCheckInCall({
                variables: {
                    id: Number.parseInt(checkInCallId, 10),
                    newDate: values?.cicDatePicker,
                },
            });
        } else {
            await deleteSuccessCheckInCall({
                variables: {
                    id: Number.parseInt(checkInCallId, 10),
                },
            });
        }

        setIsOpen(false);
    };

    const onOpenClosePDFModal = () => {
        setIsPDFModalOpen(!isPDFModalOpen);
    };

    const params = {
        countryName: data?.merchantInformation?.outlets?.[0]?.tradingAddress?.country,
        eircode: data?.merchantInformation?.outlets?.[0]?.tradingAddress?.eircode,
    };

    const { data: addressData } = useQuery(GET_ADDRESS_COORDINATES, {
        skip: !params?.eircode,
        variables: {
            eircode: params?.eircode,
            country: params?.countryName,
        },
        onError: () => {
            showErrorToast('Unable to retrieve location.');
        },
    });

    const { name = null, latitude = null, longitude = null } = addressData?.addressCoordinate ?? {};

    const checkInCallData = checkInCallResponse?.getSuccessCheckInCall;

    const PANEL_HEIGHT = 525;

    if (loading || !data) return <Loader />;

    return (
        <>
            <TitleSection
                serviceId={data?.service?.serviceNumber || serviceId}
                onCompleteCall={() => {
                    setIsOpen(true);
                }}
            />
            <Box display="flex" gap={2} mt={2}>
                <MerchantInfoColumn
                    tradingName={data.tradingName}
                    merchantAccountNumber={data.merchantAccountNumber}
                    mid={data.mid}
                    partnerName={data.partner.business.legalName}
                    partnerId={data.partnerId}
                    height={PANEL_HEIGHT}
                    flexDirection="row"
                />
            </Box>

            <Box display="flex" gap={2} mt={2}>
                <OutletSection
                    height={PANEL_HEIGHT}
                    outlets={data?.merchantInformation?.outlets}
                    useFullWidth
                />
            </Box>
            <ServiceDetailsSection
                principals={data.merchantInformation.principals}
                contractUrl={data?.agreementDocumentUrl}
                openPDFModal={onOpenClosePDFModal}
                terminalId={data.terminalId}
                orderPrimary={data.serviceRequirements?.productOrder?.primaryProducts}
                orderSecondary={data.serviceRequirements?.productOrder?.secondaryProducts}
                orderPrimaryUrls={data.serviceRequirements?.order?.primary}
                orderSecondaryUrls={data.serviceRequirements?.order?.secondary}
                merchantId={data?.merchant?.id}
            />
            <NotesSection
                merchantId={data?.merchant?.id}
                subject={checkInCallData?.callName}
                checkInCallServiceId={Number.parseInt(serviceId, 10)}
            />

            <ViewPDFModal
                title="Card Service Agreement PDF"
                fileName="Card Service Agreement"
                file={data?.agreementDocumentBytes}
                fileURL={data?.agreementDocumentUrl}
                isModalOpen={isPDFModalOpen}
                setIsModalOpen={onOpenClosePDFModal}
            />

            <ModalBox
                open={isOpen}
                handleCloseModal={() => {
                    return setIsOpen(false);
                }}
                title="Service Call"
                widthSize="640px"
            >
                <Formik
                    initialValues={{
                        cicFollowUp: 'NO',
                        cicNotes: '',
                        cicDatePicker: today,
                    }}
                    validationSchema={Yup.object().shape({
                        cicFollowUp: Yup.string()
                            .oneOf(OPTION_TYPES, 'Invalid option')
                            .required('Required')
                            .nullable(),
                        cicNotes: Yup.string()
                            .min(10, 'Notes must be at least 10 characters')
                            .required('Required'),
                        cicDatePicker: Yup.date()
                            .typeError('Please enter a valid date (e.g., DD/MM/YYYY)')
                            .min(today, 'Date cannot be in the past')
                            .required('Date is required'),
                    })}
                    onSubmit={async (values) => {
                        handleCloseCallSubmit(values);
                    }}
                >
                    {({ handleSubmit, errors, setTouched, values, setFieldValue }) => {
                        return (
                            <Box display="flex" flexDirection="column">
                                <Box display={'flex'} flexDirection={'row'} marginTop={2}>
                                    <Typography flex={1} fontSize={21} fontWeight={500}>
                                        Check-in Call Name: {checkInCallData?.callName}
                                    </Typography>
                                </Box>
                                <Box
                                    display={'flex'}
                                    flexDirection={'row'}
                                    width={'100%'}
                                    marginTop={2}
                                >
                                    <Box display="flex" flexDirection="column" flex={1}>
                                        <TextAreaInput
                                            name="cicNotes"
                                            label="Call Notes:"
                                            placeholder="Check-in Call Notes"
                                            inputStyle={{ style: { width: '100%' } }}
                                        />
                                    </Box>
                                </Box>
                                <Box display={'flex'} flexDirection={'row'} marginTop={2}>
                                    <CustomRadioGroup
                                        name="cicFollowUp"
                                        options={FOLLOW_UP_OPTIONS}
                                        label="Follow Up Needed :"
                                    />
                                </Box>

                                {
                                    // If follow-up is needed, show the date picker
                                    values.cicFollowUp === 'YES' && (
                                        <Box
                                            display={'flex'}
                                            flexDirection={'column'}
                                            marginTop={2}
                                        >
                                            <NPISingleDatePicker
                                                value={values.cicDatePicker}
                                                onChange={(date, context) => {
                                                    setFieldValue('cicDatePicker', date);
                                                }}
                                                minDate={today}
                                                maxDate={null}
                                                disableFuture={false}
                                                data-testid="cic-date-picker"
                                            />
                                            {errors.cicDatePicker && (
                                                <label className="error">
                                                    {errors.cicDatePicker}
                                                </label>
                                            )}
                                        </Box>
                                    )
                                }

                                <Box
                                    display={'flex'}
                                    flexDirection={'row'}
                                    justifyContent={'flex-end'}
                                    mt={2}
                                >
                                    <Button
                                        variant="npi-primary"
                                        onClick={() => {
                                            setIsOpen(false);
                                        }}
                                        sx={{ width: '140px' }}
                                    >
                                        Close
                                    </Button>
                                    <Button
                                        variant="npi-primary"
                                        onClick={(event) => {
                                            event.preventDefault();
                                            if (Object.keys(errors).length > 0) {
                                                showErrorToast('Please fill in required fields');
                                                if (values.cicFollowUp === 'YES') {
                                                    setTouched({
                                                        cicNotes: true,
                                                        cicFollowUp: true,
                                                        cicDatePicker: true,
                                                    });
                                                } else {
                                                    setTouched({
                                                        cicNotes: true,
                                                        cicFollowUp: true,
                                                    });
                                                }
                                            } else {
                                                handleSubmit();
                                            }
                                        }}
                                        sx={{ width: '140px', marginLeft: '16px' }}
                                        disabled={isEditing || isDeleting}
                                        loading={isEditing || isDeleting}
                                        data-testid="complete-call-button"
                                    >
                                        Complete Call
                                    </Button>
                                </Box>
                            </Box>
                        );
                    }}
                </Formik>
            </ModalBox>
        </>
    );
};

export default CheckInCallDashboardPage;
